import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Box, ChevronDown, Github, HardDrive, Image as ImageIcon, KanbanSquare, Link2, MoreHorizontal, Rocket, Slack, Trash2, Undo2,
} from 'lucide-react';
import { apiService } from '../../services/api';
import SlackChannelDialog from '../SlackChannelDialog';
import CadDialog from './CadDialog';
import CodeDialog from './CodeDialog';
import { InlineText } from './inline';
import { circleChoices } from './team';

// En-tête de la nouvelle fiche : image, nom et numéro (modifiables sur place), client, cercle, statut, et les accès
// directs (Drive, Slack, code sur GitHub, CAO dans Fusion, kickoff, backlog). Le reste est dans le menu « … ».

const STATUS = {
  proposition: ['Proposition', 'bg-amber-50 text-amber-700 ring-amber-200'],
  in_progress: ['En cours', 'bg-emerald-50 text-emerald-700 ring-emerald-200'],
  completed: ['Terminé', 'bg-slate-100 text-slate-600 ring-slate-200'],
};

const apiBaseUrl = process.env.NODE_ENV === 'development'
  ? `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}`
  : `${process.env.REACT_APP_API_URL}/api`;

const imageUrl = (url) => (!url ? null : /^https?:/.test(url) ? url : `${apiBaseUrl}${url.startsWith('/') ? '' : '/'}${url}`);

// Un lien Slack ouvre l'application : l'onglet intermédiaire de slack.com se referme tout seul.
const openLink = (url) => {
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened && url.includes('slack.com')) {
    [600, 1200, 1800, 2400].forEach((delay) => setTimeout(() => {
      try { if (!opened.closed) opened.close(); } catch { /* autre origine */ }
    }, delay));
  }
};

const LinkButton = ({ icon: Icon, label, url, alert, onEmpty }) => (
  <button type="button" onClick={() => (url ? openLink(url) : onEmpty())} title={alert || (url ? label : `${label} : à relier`)}
    className={`relative flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition ${url
      ? 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:text-slate-900'
      : 'border-dashed border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'}`}>
    <Icon size={14} />
    <span className="hidden md:inline">{label}</span>
    {alert && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-400" />}
  </button>
);

const Menu = ({ items, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const close = (event) => { if (ref.current && !ref.current.contains(event.target)) onClose(); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [onClose]);
  return (
    <div ref={ref} className="absolute right-0 top-full z-40 mt-1.5 w-60 rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
      {items.filter(Boolean).map(({ icon: Icon, label, onClick, danger }) => (
        <button key={label} type="button" onClick={() => { onClose(); onClick(); }}
          className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm ${danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50'}`}>
          <Icon size={15} className={danger ? 'text-rose-400' : 'text-slate-400'} /> {label}
        </button>
      ))}
    </div>
  );
};

const PageHeader = ({ project, clients, circles, user, actions, navigate, extra = null }) => {
  const [menu, setMenu] = useState(false);
  const [slackDialog, setSlackDialog] = useState(false);
  const [slackStatus, setSlackStatus] = useState(null);
  // Retour de GitHub après le partage d'un dépôt (?code=1) : la fenêtre Code se rouvre.
  const [codeDialog, setCodeDialog] = useState(() => new URLSearchParams(window.location.search).has('code'));
  const [codeLinks, setCodeLinks] = useState([]);
  // Retour de la connexion Autodesk (?cao=1) : la fenêtre CAO se rouvre.
  const [cadDialog, setCadDialog] = useState(() => new URLSearchParams(window.location.search).has('cao'));
  const [cadLinks, setCadLinks] = useState([]);
  const [editingClient, setEditingClient] = useState(false);
  const fileInput = useRef(null);
  const [label, tone] = STATUS[project.status] || [project.status_display || project.status, 'bg-slate-100 text-slate-600 ring-slate-200'];
  const choices = useMemo(() => circleChoices(project, circles, [user?.id, user?.employee_id]), [project, circles, user]);
  const circle = circles.find((c) => Number(c.id) === Number(project.circle_id));
  const hero = imageUrl(project.hero_image_url);

  useEffect(() => {
    if (!project.slack_channel_url) { setSlackStatus(null); return; }
    apiService.get(`/projects/${project.id}/slack`).then((data) => setSlackStatus(data?.status || null)).catch(() => setSlackStatus(null));
  }, [project.id, project.slack_channel_url]);

  useEffect(() => {
    apiService.get(`/projects/${project.id}/github`).then((data) => setCodeLinks(data?.links || [])).catch(() => setCodeLinks([]));
    apiService.get(`/projects/${project.id}/fusion`).then((data) => setCadLinks(data?.links || [])).catch(() => setCadLinks([]));
  }, [project.id]);

  const editLink = (field, name) => {
    const value = window.prompt(`Lien ${name} (vide pour retirer)`, project[field] || '');
    if (value === null) return;
    actions.patchProject({ [field]: value.trim() || null }).catch(() => {});
  };

  const openBacklog = () => {
    try {
      localStorage.setItem('objectiveBoard_viewMode', 'project');
      localStorage.setItem('objectiveBoard_selectedProject', String(project.id));
    } catch {
      // le backlog s'ouvrira sur sa vue par défaut
    }
    navigate('/objectives');
  };

  const slackAlert = slackStatus && !slackStatus.watching
    ? `Nexus ne suit pas ce channel${{ not_in_channel: ' : ajoute-le dans Slack (/invite @nexus)',
      missing_scope: " : l'app Slack doit recevoir le droit de rejoindre les channels" }[slackStatus.error] || ''}`
    : null;

  return (
    <header className="flex flex-wrap items-start gap-x-4 gap-y-3">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <button type="button" onClick={() => navigate('/projects')} aria-label="Retour aux projets"
          className="mt-1.5 hidden rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-700 sm:block">
          <ArrowLeft size={18} />
        </button>
        <button type="button" onClick={() => fileInput.current?.click()} title="Changer l'image du projet"
          className="group relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-sm sm:h-14 sm:w-14">
          {hero ? <img src={hero} alt="" className="h-full w-full object-cover" />
            : <span className="flex h-full w-full items-center justify-center text-lg font-semibold text-white">{(project.name || '?').slice(0, 1).toUpperCase()}</span>}
          <span className="absolute inset-0 flex items-center justify-center bg-slate-900/40 text-white opacity-0 transition group-hover:opacity-100"><ImageIcon size={16} /></span>
        </button>
        <input ref={fileInput} type="file" accept="image/*" className="hidden"
          onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) actions.uploadHero(file).catch(() => {}); }} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <InlineText value={project.name} required ariaLabel="Nom du projet" onSave={(name) => actions.patchProject({ name }).catch(() => {})}
              className="max-w-full text-xl font-semibold text-slate-900 sm:text-2xl" autoSize />
            <span className="flex items-center rounded-md bg-slate-100 px-1.5 text-xs font-medium text-slate-500">
              #<InlineText value={project.project_number} placeholder="n°" ariaLabel="Numéro du projet"
                onSave={(project_number) => actions.patchProject({ project_number }).catch(() => {})}
                className="py-0.5 text-xs font-medium text-slate-600" autoSize />
            </span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${tone}`}>{label}</span>
            {extra}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
            {editingClient ? (
              <select autoFocus value={project.client_id || ''} onBlur={() => setEditingClient(false)}
                onChange={(e) => {
                  setEditingClient(false);
                  actions.patchProject({ client_id: e.target.value ? Number(e.target.value) : null }).catch(() => {});
                }}
                className="max-w-[16rem] rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-sm">
                <option value="">Sans client</option>
                {clients.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            ) : (
              <button type="button" onClick={() => setEditingClient(true)} title="Changer de client"
                className="rounded-md font-medium text-slate-700 hover:text-indigo-700">
                {project.client_name || <span className="text-slate-400">Sans client</span>}
              </button>
            )}
            <span className="text-slate-300">·</span>
            {choices.length ? (
              <label className="relative inline-flex items-center gap-1 text-slate-500 hover:text-slate-800" title="Cercle du projet">
                {circle?.name || 'Sans cercle'}
                <ChevronDown size={13} className="text-slate-400" />
                <select value={project.circle_id || ''} aria-label="Cercle du projet"
                  onChange={(e) => actions.patchProject({ circle_id: e.target.value ? Number(e.target.value) : null }).catch(() => {})}
                  className="absolute inset-0 cursor-pointer opacity-0">
                  {!choices.some((c) => Number(c.id) === Number(project.circle_id)) && <option value={project.circle_id || ''}>{circle?.name || 'Sans cercle'}</option>}
                  {choices.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            ) : (
              <span title="Cercle du projet">{circle?.name || 'Sans cercle'}</span>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto">
        <LinkButton icon={HardDrive} label="Drive" url={project.google_drive_url} onEmpty={() => editLink('google_drive_url', 'Google Drive')} />
        <LinkButton icon={Slack} label="Slack" url={project.slack_channel_url} alert={slackAlert} onEmpty={() => setSlackDialog(true)} />
        <button type="button" onClick={() => setCodeDialog(true)}
          title={codeLinks.length ? `Code : ${codeLinks.map((l) => l.repo).join(', ')}` : 'Code : relier un dépôt GitHub'}
          className={`relative flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition ${codeLinks.length
            ? 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:text-slate-900'
            : 'border-dashed border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'}`}>
          <Github size={14} />
          <span className="hidden md:inline">Code</span>
          {codeLinks.some((l) => l.error) && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-400" />}
        </button>
        <button type="button" onClick={() => setCadDialog(true)}
          title={cadLinks.length ? `CAO : ${cadLinks.map((l) => l.name).join(', ')}` : 'CAO : relier un projet Fusion'}
          className={`relative flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition ${cadLinks.length
            ? 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:text-slate-900'
            : 'border-dashed border-slate-200 text-slate-400 hover:border-slate-300 hover:text-slate-600'}`}>
          <Box size={14} />
          <span className="hidden md:inline">CAO</span>
          {cadLinks.some((l) => l.error) && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-amber-400" />}
        </button>
        <button type="button" onClick={openBacklog}
          className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:border-slate-300">
          <KanbanSquare size={14} /> Backlog
        </button>
        <button type="button" title={project.status === 'proposition' ? 'Kickoff de lancement, en direct avec Nexus' : 'Sprints, tokens et responsables'}
          onClick={() => navigate(`/project/${project.id}/kickoff${project.status === 'proposition' ? '/lancement' : ''}`)}
          className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 hover:border-slate-300">
          <Rocket size={14} /> <span className="hidden md:inline">Kickoff</span>
        </button>
        <div className="relative">
          <button type="button" onClick={() => setMenu((v) => !v)} aria-label="Plus d'actions"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-white hover:text-slate-800">
            <MoreHorizontal size={18} />
          </button>
          {menu && (
            <Menu onClose={() => setMenu(false)} items={[
              { icon: Link2, label: 'Lien Google Drive…', onClick: () => editLink('google_drive_url', 'Google Drive') },
              { icon: Link2, label: 'Lien Slack…', onClick: () => editLink('slack_channel_url', 'Slack') },
              { icon: ImageIcon, label: "Changer l'image", onClick: () => fileInput.current?.click() },
              project.hero_image_url && { icon: Trash2, label: "Retirer l'image", onClick: () => actions.removeHero().catch(() => {}), danger: true },
              { icon: Undo2, label: 'Ancienne fiche', onClick: () => navigate(`/project/${project.id}?ancienne=1`) },
            ]} />
          )}
        </div>
      </div>

      <CadDialog projectId={project.id} open={cadDialog} onClose={() => setCadDialog(false)} onChange={setCadLinks} />
      <CodeDialog projectId={project.id} open={codeDialog} onClose={() => setCodeDialog(false)} onChange={setCodeLinks} />
      <SlackChannelDialog projectId={project.id} open={slackDialog} onClose={() => setSlackDialog(false)}
        onCreated={(link) => actions.patchProject({ slack_channel_url: link }).catch(() => {})}
        onLinkExisting={() => editLink('slack_channel_url', 'Slack')} />
    </header>
  );
};

export default PageHeader;
